import { Alert, Box, Button, Card, CardContent, Grid, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import PartnerCommissionApi from "../../../infrastructure/api/partnerCommissionApi";
import { getEnumLabel, TRIGGER_EVENT_LABELS } from "../SLN/market-growth/constants/commissionEnums";
import PartnerCommissionPageShell from "./components/PartnerCommissionPageShell";
import PartnerStatCard from "./components/PartnerStatCard";
import SimpleBreakdownTable from "./components/SimpleBreakdownTable";
import { PARTNER_COMMISSION_STATUS_LABELS } from "./constants/portalEnums";
import { formatMoney } from "./utils/formatMoney";
import { isPartnerLevel1 } from "./utils/portalHelpers";

const formatMonth = (year, month) => `${year}/${month}`;

const PartnerCommissionDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noPartnerProfile, setNoPartnerProfile] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoPartnerProfile(false);
    try {
      const res = await PartnerCommissionApi.getDashboard();
      const normalized = PartnerCommissionApi.normalize(
        res,
        "بارگذاری داشبورد ناموفق بود."
      );
      if (normalized.noPartnerProfile) {
        setNoPartnerProfile(true);
        setData(null);
        return;
      }
      if (normalized.error) {
        setError(normalized.error);
        setData(null);
        return;
      }
      setData(normalized.data);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const monthlyRows = useMemo(
    () =>
      (data?.monthlyCommissionTrend || []).map((item, index) => ({
        id: `${item.year}-${item.month}-${index}`,
        period: formatMonth(item.year, item.month),
        amount: formatMoney(item.amount),
      })),
    [data]
  );

  const statusRows = useMemo(
    () =>
      (data?.commissionByStatus || []).map((item, index) => ({
        id: `${item.status}-${index}`,
        status: getEnumLabel(PARTNER_COMMISSION_STATUS_LABELS, item.status),
        count: item.count,
        amount: formatMoney(item.amount),
      })),
    [data]
  );

  const triggerRows = useMemo(
    () =>
      (data?.commissionByTriggerEvent || []).map((item, index) => ({
        id: `${item.triggerEvent}-${index}`,
        trigger: getEnumLabel(TRIGGER_EVENT_LABELS, item.triggerEvent),
        count: item.count,
        amount: formatMoney(item.amount),
      })),
    [data]
  );

  return (
    <PartnerCommissionPageShell
      title="داشبورد من"
      description="خلاصه وضعیت پورسانت، تیم و فروش."
      loading={loading}
      error={error}
      noPartnerProfile={noPartnerProfile}
      onRetry={load}
    >
      {data?.parentPartnerDisplayName ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          مدیر فروش شما: <strong>{data.parentPartnerDisplayName}</strong>
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <PartnerStatCard label="کل پورسانت" value={formatMoney(data?.totalCommissions)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <PartnerStatCard label="پورسانت در انتظار" value={formatMoney(data?.pendingCommissions)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <PartnerStatCard label="پورسانت تأییدشده" value={formatMoney(data?.approvedCommissions)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <PartnerStatCard label="قابل تسویه" value={formatMoney(data?.payableCommissions)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <PartnerStatCard label="پرداخت‌شده" value={formatMoney(data?.paidCommissions)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <PartnerStatCard label="ردشده" value={formatMoney(data?.rejectedCommissions)} />
        </Grid>
        {isPartnerLevel1({ partnerLevel: data?.partnerLevel }) ? (
          <>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <PartnerStatCard label="تعداد اعضای تیم" value={data?.totalTeamMembers ?? 0} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <PartnerStatCard label="فروش تیم" value={data?.totalTeamSalesCount ?? 0} />
            </Grid>
          </>
        ) : null}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <PartnerStatCard label="فروش مستقیم" value={data?.totalDirectSalesCount ?? 0} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <PartnerStatCard
            label="پورسانت ماه جاری"
            value={formatMoney(data?.currentMonthCommission)}
          />
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
                sx={{ mb: 2 }}
              >
                <Typography variant="h6" fontWeight={700}>
                  لیدهای معرفی شده
                </Typography>
                <Button
                  component={RouterLink}
                  to="/SLN/Partner/PartnerLeads"
                  variant="outlined"
                  size="small"
                >
                  مشاهده همه
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <PartnerStatCard
                    label="کل لیدها"
                    value={data?.leadSummary?.totalLeads ?? 0}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <PartnerStatCard
                    label="ثبت‌نام‌شده"
                    value={data?.leadSummary?.registeredLeads ?? 0}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <PartnerStatCard
                    label="با درخواست نیروگاه"
                    value={data?.leadSummary?.leadsWithProjectRequest ?? 0}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <PartnerStatCard
                    label="تبدیل‌شده"
                    value={data?.leadSummary?.convertedLeads ?? 0}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <SimpleBreakdownTable
            title="روند ماهانه پورسانت"
            columns={[
              { key: "period", label: "ماه" },
              { key: "amount", label: "مبلغ" },
            ]}
            rows={monthlyRows}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SimpleBreakdownTable
            title="وضعیت پورسانت‌ها"
            columns={[
              { key: "status", label: "وضعیت" },
              { key: "count", label: "تعداد" },
              { key: "amount", label: "مبلغ" },
            ]}
            rows={statusRows}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SimpleBreakdownTable
            title="پورسانت بر اساس رویداد"
            columns={[
              { key: "trigger", label: "رویداد" },
              { key: "count", label: "تعداد" },
              { key: "amount", label: "مبلغ" },
            ]}
            rows={triggerRows}
          />
        </Grid>
      </Grid>
    </PartnerCommissionPageShell>
  );
};

export default PartnerCommissionDashboardPage;
